import React, { useCallback, useEffect, useState } from 'react';
import Search from '../../shared/components/ui/Search';
import Table from '../../shared/components/layout/Table';
import Modal from '../../shared/components/ui/Modal';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getConsolidatedUnitList,
  getUnitStatus,
  updateUnitStatus
} from '../services/api/implementation/impl';
import { useForm } from 'react-hook-form';
import { UpdateStatus } from '../components/forms/UpdateStatus';
import { transformData } from '../../super-admin/utils/transformData';
import { filterData } from '../../shared/utils/filterData';
import FormInput from '../../shared/components/ui/FormInput';
import NoDataFound from '../components/layout/NoDataFound';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import toast from 'react-hot-toast';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Headroom from 'react-headroom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../shared/components/ui/Loader';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const ConsolidatedList = () => {
  const userId = Cookies.get('userId');
  const { register } = useForm();
  const { collapsed } = useSidebar();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  // State Variables
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [selectedConsolidatedItemId, setSelectedConsolidatedItemId] = useState(null);

  // Keep track of possible statuses from the backend
  const [updateStatusOptions, setUpdateStatusOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For local data filtering
  const [ConsolidateFilter, setConsolidateFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // For filters
  const [StatusID, setStatusID] = useState('');
  const [ProjectID, setProjectID] = useState(null);

  // Active filters state
  const [activeFilters, setActiveFilters] = useState({
    status: null,
    project: null
  });

  // Fetch consolidated list data
  const {
    data: consolidatedData,
    refetch: refetchConsolidatedList,
    isLoading
  } = useQuery({
    queryKey: ['consolidatedUnitList', StatusID, ProjectID],
    queryFn: () => getConsolidatedUnitList(ProjectID, StatusID, 1, 1000000)
  });

  // Fetch the unit statuses (like in UnitPricingList)
  const { data: unitStatus, refetch: refetchUnitStatus } = useQuery({
    queryKey: ['unitStatus'],
    queryFn: () => getUnitStatus()
  });

  console.log('unitStatus', unitStatus);

  /**
   * Reuse the SAME mutation style from UnitPricingList:
   * handleUnitStatusUpdate -> calls updateUnitStatus(selectedItemId, payload)
   */
  const { mutate: handleUnitStatusUpdate } = useMutation({
    mutationFn: (payload) => updateUnitStatus(selectedConsolidatedItemId, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async () => {
      toast.success('Unit status updated successfully');
      setIsSubmitting(false);
      setOpenUpdateStatus(false);
      await refetchConsolidatedList();
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating unit status');
      console.error(err?.response?.data?.message || err?.message || 'Error updating unit status');
    }
  });

  // Once we have the unitStatus data, update our local state of possible statuses

  // Track page view
  useEffect(() => {
    mixpanel.track('Consolidated List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (unitStatus) {
      refetchUnitStatus();
      // Example: filter out or transform statuses as needed
      const modifiedArray = unitStatus?.filter(
        (item) =>
          item !== 'Booked' &&
          item !== 'Vacant' &&
          item !== 'Blocked_for_Broker' &&
          item !== 'Temporary_Booked' &&
          item !== 'Blocked'
      );
      setUpdateStatusOptions(modifiedArray);
    }
  }, [unitStatus, refetchUnitStatus]);

  // Keep local filter data in sync with the main data
  useEffect(() => {
    if (consolidatedData) {
      setConsolidateFilter(consolidatedData);
    }
  }, [consolidatedData]);

  // Manually re-fetch consolidated list if needed
  useEffect(() => {
    // (In most cases, you'd only want to call refetchConsolidatedList on certain triggers,
    //  not every time consolidatedData changes. But leaving your original structure.)
    refetchConsolidatedList();
  }, [consolidatedData, refetchConsolidatedList]);

  // Function to clear specific filter
  const clearFilter = (filterType) => {
    if (filterType === 'status') {
      setStatusID('');
      const newFilters = { ...activeFilters, status: null };
      setActiveFilters(newFilters);
    } else if (filterType === 'project') {
      setProjectID(null);
      const newFilters = { ...activeFilters, project: null };
      setActiveFilters(newFilters);
    }

    // Track filter clear
    mixpanel.track('Consolidated List Filter Cleared', {
      user_id: userId,
      filter_type: filterType,
      timestamp: new Date().toISOString()
    });
  };

  // Project filter handler
  const handleProjectFilter = (id) => {
    setProjectID(id || null);
    const selectedProject = projects?.find((p) => p.id === id);
    const newFilters = { ...activeFilters, project: selectedProject?.name || null };
    setActiveFilters(newFilters);
    mixpanel.track('Consolidated List Filter Changed', {
      user_id: userId,
      filter_type: 'project',
      project_id: id,
      timestamp: new Date().toISOString()
    });
  };

  // Table header format
  const ConsolidatedDataFormat = [
    'id',
    'customer_name',
    'project_name',
    'unit_name',
    'complete_payment_stage',
    'paid_amount',
    'pending_amount',
    'salesPerson',
    'status',
    'actions' // We'll add an Action column
  ];

  // Transform data for the table
  const baseData = Array.isArray(ConsolidateFilter)
    ? ConsolidateFilter
    : Array.isArray(consolidatedData)
      ? consolidatedData
      : [];
  const ConsolidatedData =
    baseData.length > 0
      ? baseData.map((item) => {
          const transformedItem = transformData(
            {
              id: item?.unit?.id,
              customer_name: item?.leadName || 'N/A',
              project_name: item?.block?.project?.name || 'N/A',
              unit_name: item?.unitName || 'N/A',
              complete_payment_stage: item?.completedPaymentStage === 100 ? 'Completed' : 'Pending',
              paid_amount: item?.total_paid_amount || 0,
              pending_amount: item?.due_amount || 0,
              salesPerson: item?.salesPerson || '-',
              status: item?.unitStatus?.replace(/_/g, ' ')?.trim() || '-'
            },
            ConsolidatedDataFormat
          );
          return transformedItem;
        })
      : [];

  // Search
  const handleSearchChange = useCallback(
    (incomingQuery) => {
      setSearchQuery(incomingQuery);
      const source = Array.isArray(consolidatedData) ? consolidatedData : [];
      if (incomingQuery) {
        const filteredData = filterData('leadName', source, incomingQuery);
        setConsolidateFilter(filteredData);
      } else {
        setConsolidateFilter(source);
      }
    },
    [consolidatedData]
  );

  // Clicking Action -> open the "Update Status" modal
  const handleStatusUpdateClick = (id) => {
    console.log('Update status for ID:', id);
    setSelectedConsolidatedItemId(id);
    setOpenUpdateStatus(true);
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      {openUpdateStatus && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Update Status"
          onClose={() => setOpenUpdateStatus(false)}>
          <UpdateStatus
            onClose={() => setOpenUpdateStatus(false)}
            statuses={updateStatusOptions}
            isSubmitting={isSubmitting}
            onSubmit={handleUnitStatusUpdate}
            type={'unitPricing'}
          />
        </Modal>
      )}

      <div className="w-full flex flex-col bg-gray-100 pb-10 overflow-hidden hide-scrollbar">
        <Headroom
          disable
          style={{
            position: 'fixed',
            top: '4.6rem',
            left: collapsed ? '76px' : '16.6%',
            right: '0',
            zIndex: 10,
            transition: 'all 0.3s ease-in-out'
          }}>
          <div className="flex items-center justify-between px-6 py-4 bg-white border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out">
            <div className="flex flex-row gap-5 justify-start items-center">
              <Search query={searchQuery} onChange={handleSearchChange} />

              {/* Filter Indicators */}
              {(activeFilters.status || activeFilters.project) && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">Filtering by:</span>
                  <div className="flex items-center gap-1">
                    {activeFilters.status && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {activeFilters.status}
                        <button
                          onClick={() => clearFilter('status')}
                          className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                          ×
                        </button>
                      </span>
                    )}
                    {activeFilters.project && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {activeFilters.project}
                        <button
                          onClick={() => clearFilter('project')}
                          className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Headroom>

        <div className="mt-[4rem]  w-full h-full overflow-clip bg-gray-100">
          <GridWrapper width={100}>
            <div className="mt-10 overflow-scroll hide-scrollbar table-container">
              <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
              <Table
                headers={[
                  'Customer Name',
                  {
                    label: 'Project Name',
                    dropdown: true,
                    dropdownOptions: [
                      { id: '', value: '', label: 'Select Project', selected: true },
                      ...projectsForDropdown
                    ],
                    onDropdownSelect: (id) => handleProjectFilter(id)
                  },
                  'Unit Name',
                  'Complete Payment Stage',
                  'Paid Amount',
                  'Pending Amount',
                  'Sales Person',
                  {
                    label: 'Status',
                    dropdown: true,
                    dropdownOptions: [
                      { id: '', value: '', label: 'Select Status', selected: true },
                      ...(Array.isArray(unitStatus) ? unitStatus : []).map((item) => ({
                        id: item,
                        value: item,
                        label: item
                      }))
                    ],
                    onDropdownSelect: (id) => {
                      setStatusID(id);

                      if (!id) {
                        const newFilters = { ...activeFilters, status: null };
                        setActiveFilters(newFilters);
                      } else {
                        const newFilters = {
                          ...activeFilters,
                          status: id
                        };
                        setActiveFilters(newFilters);
                      }

                      mixpanel.track('Consolidated List Filter Changed', {
                        user_id: userId,
                        filter_type: 'status',
                        status_id: id,
                        timestamp: new Date().toISOString()
                      });
                    }
                  },
                  'Action'
                ]}
                data={ConsolidatedData}
                identifier={'id'}
                // IMPORTANT: Enable status update so the Table
                // can render the "Action" column with a button
                isEditEnable={false}
                isStatusUpdateEnable={true}
                onStatusUpdate={handleStatusUpdateClick}
              />
            </div>
          </GridWrapper>
        </div>
      </div>
    </>
  );
};

export default ConsolidatedList;
